import { Colors, Icon, type OptionProps } from '@amelio/ui-library';

import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { CommonActions, EvaluationActions } from '@/constants/Actions';
import {
    CycleEvaluationStatus,
    EvaluationType,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleEvaluationGroupSteps } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import { CycleEvaluationSteps } from '@/constants/cycle-evaluation/CycleEvaluationSteps';
import { useAmelioTranslations } from '@/hooks';
import {
    resolveLegacyCycleName,
    selectGroupEvaluationTemplates,
    selectGroupSentCyclesEvaluation,
    type EvaluationTemplateSummary,
} from '@/pages/CycleEvaluation/Dashboard/cycleListRows';
import { isDeactivatedGroupStatus } from '@/pages/CycleEvaluation/Dashboard/cycleStatus';
import { CycleEvaluationGroupStatus } from '@/types/cycle-evaluation/CycleEvaluationGroup';

/** What the "See sent cycles" popup needs: the cycle name titles it, the templates qualify it. */
export interface OpenSentCyclesPayload {
    cycleEvaluation: CycleEvaluation;
    cycleName: string;
    evaluationTemplates: EvaluationTemplateSummary[];
}

interface UseDashboardActionMenusParams {
    cycleEvaluationList: CycleEvaluation[] | undefined;
    isRefetching: boolean;
    isGroupListRefetching: boolean;
    isUpdatingGroup: boolean;
    isDeletingGroup: boolean;
    hasPendingStatusConfirmation: boolean;
    onEditEvaluation: (cycleEvaluation: CycleEvaluation) => void;
    onActivateEvaluation: (cycleEvaluation: CycleEvaluation) => void;
    onOpenRenameModal: (cycleEvaluation: CycleEvaluation) => void;
    onDeleteEvaluation: (cycleEvaluation: CycleEvaluation) => void;
    onEditGroup: (group: CycleEvaluationGroup) => void;
    onToggleGroupStatus: (group: CycleEvaluationGroup) => void;
    onLaunchGroup: (group: CycleEvaluationGroup) => void;
    onOpenRenameGroupModal: (group: CycleEvaluationGroup) => void;
    onDeleteGroup: (group: CycleEvaluationGroup) => void;
    onSendReminders: (group: CycleEvaluationGroup) => void;
    onOpenSentCycles: (payload: OpenSentCyclesPayload) => void;
}

export const useDashboardActionMenus = ({
    cycleEvaluationList,
    isRefetching,
    isGroupListRefetching,
    isUpdatingGroup,
    isDeletingGroup,
    hasPendingStatusConfirmation,
    onEditEvaluation,
    onActivateEvaluation,
    onOpenRenameModal,
    onDeleteEvaluation,
    onEditGroup,
    onToggleGroupStatus,
    onLaunchGroup,
    onOpenRenameGroupModal,
    onDeleteGroup,
    onSendReminders,
    onOpenSentCycles,
}: UseDashboardActionMenusParams) => {
    const { t } = useAmelioTranslations();

    const buildSeeSentCyclesItem = ({
        payload,
        disabled,
    }: {
        payload: OpenSentCyclesPayload;
        disabled: boolean;
    }): OptionProps => ({
        id: EvaluationActions.SEE_SENT_CYCLES,
        label: t('cycleEvaluationDashboard.menuItem.seeSentCycles'),
        icon: (
            <Icon
                name={'security-eye'}
                color={Colors.SECONDARY_800}
            />
        ),
        onClick: () => onOpenSentCycles(payload),
        disabled,
    });

    const actionMenuItems = (cycleEvaluation: CycleEvaluation): OptionProps[] =>
        [
            {
                id: CommonActions.EDIT,
                label: t('common.edit'),
                icon: <Icon name={'edit-edit-2'} />,
                onClick: () => onEditEvaluation(cycleEvaluation),
                disabled: isRefetching,
            },
            {
                id: CommonActions.ACTIVATE,
                label:
                    cycleEvaluation.status === CycleEvaluationStatus.ACTIVE
                        ? t('common.deactivate')
                        : t('common.activate'),
                icon: (
                    <Icon
                        name={
                            cycleEvaluation.status === CycleEvaluationStatus.ACTIVE
                                ? 'essential-close-circle'
                                : 'essential-checkmark'
                        }
                    />
                ),
                onClick: () => onActivateEvaluation(cycleEvaluation),
                disabled: isRefetching,
            },
            {
                id: CommonActions.RENAME,
                label: t('common.rename'),
                icon: <Icon name={'character-text-block'} />,
                onClick: () => onOpenRenameModal(cycleEvaluation),
                disabled: isRefetching,
            },
            {
                id: CommonActions.DELETE,
                label: t('common.delete'),
                icon: (
                    <Icon
                        name={'essential-trash'}
                        color={Colors.ERROR}
                    />
                ),
                danger: true,
                onClick: () => onDeleteEvaluation(cycleEvaluation),
                disabled: isRefetching,
            },
            // A cycle created before the redesign has no cycle name, so its template title stands
            // in as the title and no separate template line is shown.
            buildSeeSentCyclesItem({
                payload: {
                    cycleEvaluation,
                    cycleName: resolveLegacyCycleName(cycleEvaluation),
                    evaluationTemplates: [],
                },
                disabled: isRefetching,
            }),
        ].filter((item) => {
            if (item.id === EvaluationActions.SEE_SENT_CYCLES) {
                return cycleEvaluation.reviewType === EvaluationType.EVALUATION_360;
            }
            if (item.id === CommonActions.ACTIVATE) {
                return (
                    cycleEvaluation.status === CycleEvaluationStatus.ACTIVE ||
                    cycleEvaluation.currentStep === CycleEvaluationSteps.SCHEDULE_CREATION
                );
            }
            return true;
        });

    const groupActionMenuItems = (group: CycleEvaluationGroup): OptionProps[] => {
        const isBusy =
            isGroupListRefetching ||
            isUpdatingGroup ||
            isDeletingGroup ||
            hasPendingStatusConfirmation;

        const items: OptionProps[] = [
            {
                id: CommonActions.EDIT,
                label: t('common.edit'),
                icon: <Icon name={'edit-edit-2'} />,
                onClick: () => onEditGroup(group),
                disabled: isBusy,
            },
        ];

        if (group.status === CycleEvaluationGroupStatus.ACTIVE) {
            items.push({
                id: CommonActions.DEACTIVATE,
                label: t('common.deactivate'),
                icon: <Icon name={'essential-close-circle'} />,
                onClick: () => onToggleGroupStatus(group),
                disabled: isBusy,
            });
        }

        if (isDeactivatedGroupStatus(group.status)) {
            items.push({
                id: CommonActions.ACTIVATE,
                label: t('cycleEvaluationDashboard.menuItem.reactivate'),
                icon: <Icon name={'essential-checkmark'} />,
                onClick: () => onToggleGroupStatus(group),
                disabled: isBusy,
            });
        }

        if (
            group.status === CycleEvaluationGroupStatus.DRAFT &&
            group.currentStep === CycleEvaluationGroupSteps.COMMUNICATIONS
        ) {
            items.push({
                id: CommonActions.ACTIVATE,
                label: t('common.activate'),
                icon: <Icon name={'essential-checkmark'} />,
                onClick: () => onLaunchGroup(group),
                disabled: isBusy,
            });
        }

        if (group.status === CycleEvaluationGroupStatus.ACTIVE) {
            items.push({
                id: CommonActions.SEND_REMINDERS,
                label: t('cycleEvaluationDashboard.menuItem.sendReminders'),
                icon: <Icon name={'arrow-refresh'} />,
                onClick: () => onSendReminders(group),
                disabled: isBusy,
            });
        }

        if (group.status !== CycleEvaluationGroupStatus.COMPLETED) {
            items.push({
                id: CommonActions.RENAME,
                label: t('common.rename'),
                icon: <Icon name={'character-text-block'} />,
                onClick: () => onOpenRenameGroupModal(group),
                disabled: isBusy,
            });
        }

        items.push({
            id: CommonActions.DELETE,
            label: t('common.delete'),
            icon: (
                <Icon
                    name={'essential-trash'}
                    color={Colors.ERROR}
                />
            ),
            danger: true,
            onClick: () => onDeleteGroup(group),
            disabled: isBusy,
        });

        const sentCyclesEvaluationOfGroup = selectGroupSentCyclesEvaluation({
            group,
            cycleEvaluations: cycleEvaluationList ?? [],
        });

        if (sentCyclesEvaluationOfGroup) {
            items.push(
                buildSeeSentCyclesItem({
                    payload: {
                        cycleEvaluation: sentCyclesEvaluationOfGroup,
                        cycleName: group.name,
                        evaluationTemplates: selectGroupEvaluationTemplates({
                            group,
                            cycleEvaluations: cycleEvaluationList ?? [],
                        }),
                    },
                    disabled: isBusy,
                }),
            );
        }

        return items;
    };

    return { actionMenuItems, groupActionMenuItems };
};
